import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button, message, PageHeader } from 'antd';
import { useHistory } from 'react-router';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { combiningObjects } from './listFormater';
import ManageCardListConstructor from './ManageCardListConstructor/ManageCardListConstructor';

import {
  approveUnknownOcppTag,
  deleteUnknownOcppTag,
  getUnknownOcppTags,
} from '../../../hooks/ocpptags';
import { UnknownOcppTag } from '../../../types/responses/UnknownOcppTags';
import { ocppUnknownTagListFilter } from '../../../utils/ocppTags/ocppTagListFilter';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { Search } from '../../molecules/Search';
import EmptySearch from '../EmptySearch';

interface IOcppTagsTableTable {
  routes: any;
}

const UnknownOcppTagsTable: React.FC<IOcppTagsTableTable> = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setdata] = useState<any>([]);
  const [paginationData, setpaginationData] = useState<any>([]);
  const [unknownTagList, setUnknownTagList] = useState<UnknownOcppTag[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [checkeds, setCheckeds] = useState<string[]>([]);

  const [isApproveAllLoading, setApproveAllLoading] = useState(false);
  const [isDeleteAllLoading, setDeleteAllLoading] = useState(false);

  const history = useHistory();

  const childRef: any = useRef();
  const paginate = () => childRef?.current?.paginate();

  useEffect(() => {
    paginate();
  }, [paginationData]);

  type FilterOcppTags = (e: any) => void;
  const filterUsers: FilterOcppTags = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const clearSearch = () => {
    setSearchInput('');
  };

  const HEADER = useMemo(
    () => [
      {
        text: T('Tag ID'),
        sideIcon: <></>,
        action: () => {},
        size: '30%',
        dropdown: <></>,
      },
      {
        text: T('Attempts'),
        sideIcon: <></>,
        action: () => {},
        size: '10%',
        dropdown: <></>,
      },
      {
        text: T('Last attempt'),
        sideIcon: <></>,
        action: () => {},
        size: '15%',
        dropdown: <></>,
      },
      {
        text: T('Account'),
        sideIcon: <></>,
        action: () => {},
        size: '15%',
        dropdown: <></>,
      },
      {
        text: T('Station ID'),
        sideIcon: <></>,
        action: () => {},
        size: '15%',
        dropdown: <></>,
      },
      {
        text: T('Approve / Delete'),
        sideIcon: <></>,
        action: () => {},
        size: '15%',
        dropdown: <></>,
      },
    ],
    [],
  );

  const fetchUnknownOcppTags = useCallback(async () => {
    try {
      setLoading(true);

      const unknownTags = await getUnknownOcppTags();

      setUnknownTagList(unknownTags?.unknownOcppTags);

      setpaginationData(unknownTags?.unknownOcppTags);
      setdata(combiningObjects(unknownTags?.unknownOcppTags, HEADER));
    } finally {
      setLoading(false);

      setCheckeds([]);

      clearSearch();
    }
  }, [HEADER]);

  useEffect(() => {
    (async () => await fetchUnknownOcppTags())();
  }, [fetchUnknownOcppTags]);

  useEffect(() => {
    setpaginationData(ocppUnknownTagListFilter(searchInput, unknownTagList));

    setdata(
      combiningObjects(
        ocppUnknownTagListFilter(searchInput, unknownTagList),
        HEADER,
      ),
    );
  }, [searchInput, HEADER, unknownTagList]);

  const [isApproveLoading, setIsApproveLoading] = useState<string[]>([]);

  const handleApprove = async (ocppTag, tenantPk, isBulk) => {
    if (isBulk) {
      setApproveAllLoading(true);
    } else {
      setIsApproveLoading([...isApproveLoading, ocppTag]);
    }

    const [id_tag]: any = ocppTag.split(' ');

    const data = await approveUnknownOcppTag({
      id_tag,
      tenant_pk: tenantPk,
    });

    if (data !== null) {
      if (data.type === 'success') {
        message.success(`${ocppTag} - ${T('RFID Tag successfully approved!')}`);

        await fetchUnknownOcppTags();
      } else {
        message.error(
          `${ocppTag} - ${T('Error when trying to approve RFID Tag')}`,
        );
      }
    }

    if (isBulk) {
      setApproveAllLoading(false);
    } else {
      setIsApproveLoading([
        ...isApproveLoading.filter((item) => item !== ocppTag),
      ]);
    }
  };

  const [isDeleteLoading, setIsDeleteLoading] = useState<string[]>([]);

  const handleDelete = async (ocppTag, isBulk) => {
    if (isBulk) {
      setDeleteAllLoading(true);
    } else {
      setIsDeleteLoading([...isDeleteLoading, ocppTag]);
    }

    const [id_tag]: any = ocppTag.split(' ');

    const data = await deleteUnknownOcppTag(id_tag);

    if (data !== null) {
      if (data.type === 'success') {
        message.success(`${ocppTag} - ${T('RFID Tag successfully deleted!')}`);

        await fetchUnknownOcppTags();
      } else {
        message.error(
          `${ocppTag} - ${T('Error when trying to delete RFID Tag')}`,
        );
      }
    }

    if (isBulk) {
      setDeleteAllLoading(true);
    } else {
      setIsDeleteLoading([
        ...isDeleteLoading.filter((item) => item !== ocppTag),
      ]);
    }
  };

  const handleApproveAll = () => {
    checkeds.map(async (ocppTag: string) => {
      const [tag] = unknownTagList.filter((tag) =>
        tag.key.includes(ocppTag.replace(/\.\.\.$/, '')),
      );

      await handleApprove(tag.key, tag.tenantPk, true);
    });
  };

  const handleDeleteAll = () => {
    checkeds.map(async (ocppTag: string) => {
      const [tag] = unknownTagList.filter((tag) =>
        tag.key.includes(ocppTag.replace(/\.\.\.$/, '')),
      );

      await handleDelete(tag.key, true);
    });
  };

  return (
    <Div>
      <PageHeader
        onBack={history.goBack}
        title={
          <div className='header-wrapper'>
            <div className='header-title'>Tags RFID Desconhecidas</div>

            <Search
              className='search-input'
              placeholder={T('Search By TAG ID')}
              action={filterUsers}
              closeBtn={clearSearch}
              value={searchInput}
            />

            <Button
              disabled={!checkeds.length || isDeleteAllLoading}
              loading={isApproveAllLoading}
              size='large'
              type='primary'
              onClick={handleApproveAll}
            >
              {T('Approve')}
            </Button>

            <Button
              danger
              disabled={!checkeds.length || isApproveAllLoading}
              loading={isDeleteAllLoading}
              size='large'
              type='primary'
              className='delete_button'
              onClick={handleDeleteAll}
            >
              {T('Delete')}
            </Button>
          </div>
        }
      />

      {loading ? (
        <ListSkeleton
          style={{ paddingTop: 20, paddingLeft: 20, paddingRight: 20 }}
        />
      ) : !loading && !data?.cardsList?.length ? (
        <EmptySearch />
      ) : (
        <ManageCardListConstructor
          childRef={childRef}
          data={data}
          pureData={ocppUnknownTagListFilter(searchInput, paginationData)}
          paginationCallback={(newList: UnknownOcppTag[]) =>
            setdata(combiningObjects(newList, HEADER))
          }
          checkeds={checkeds}
          setCheckeds={setCheckeds}
          handleApprove={handleApprove}
          isApproveLoading={isApproveLoading}
          handleDelete={handleDelete}
          isDeleteLoading={isDeleteLoading}
          isAllLoading={isApproveAllLoading || isDeleteAllLoading}
        />
      )}
    </Div>
  );
};

export default UnknownOcppTagsTable;

const Div = styled.div`
  padding-bottom: 70px;

  .ant-page-header-heading {
    justify-content: center;
  }

  .header-wrapper {
    display: flex;
    align-items: center;
  }

  .header-title {
    margin-right: 20px;
  }

  button {
    margin-left: 10px;
  }

  .search-input {
    width: 500px;
    margin-right: 10px;
    margin-bottom: 0%;
  }
`;
